import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchReplayList, type ReplayListItem } from "../../shared/admin-replays";
import styles from "./ReplaysPage.module.scss";

// Admin-only: every finished online match the host has stored, newest first.
// A row opens the replay viewer (ReplayWatchPage), death forensics included.

const BOX_NAMES: Record<string, string> = { small: "Duel", medium: "Brawl", large: "Royale Chaos" };

function when(ms: number | null): string {
  if (!ms) return "";
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
function kindLabel(it: ReplayListItem): string {
  const s = it.summary;
  if (s.kind === "public") return BOX_NAMES[s.base || ""] || s.base || "Public";
  return s.kind === "custom" ? "Custom lobby" : s.kind === "solo" ? "Instant match" : "Room";
}

export default function ReplaysPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ReplayListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | string>("loading");
  const [onlyReports, setOnlyReports] = useState(false);

  async function load(next?: string | null) {
    setState("loading");
    const res = await fetchReplayList(next);
    if (res.error) return setState(res.error);
    if (res.unconfigured) return setState("unconfigured");
    setItems((prev) => (next ? [...prev, ...res.items] : res.items));
    setCursor(res.cursor);
    setState("ready");
  }
  useEffect(() => { load(); }, []);

  const shown = onlyReports ? items.filter((it) => it.summary.deathReports > 0) : items;

  return (
    <div className={styles.page}>
      <div className={styles.bar}>
        <label className={styles.check}>
          <input type="checkbox" checked={onlyReports} onChange={(e) => setOnlyReports(e.target.checked)} /> Only matches with death reports
        </label>
        <button className={styles.btn} onClick={() => load()}>Refresh</button>
      </div>
      {state === "unconfigured" && <p className={styles.state}>This server has no replay bucket bound (REPLAYS), so nothing is being stored.</p>}
      {state !== "ready" && state !== "loading" && state !== "unconfigured" && <p className={styles.state}>Could not load replays ({state}).</p>}
      {state === "ready" && shown.length === 0 && <p className={styles.state}>No replays yet. Every online match that finishes from now on shows up here.</p>}
      {shown.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr><th>Ended</th><th>Match</th><th>Map</th><th>Players</th><th>Rounds</th><th>Humans</th><th>Reports</th><th>Size</th></tr>
          </thead>
          <tbody>
            {shown.map((it) => (
              <tr key={it.key} onClick={() => navigate("/admin/replays/watch?key=" + encodeURIComponent(it.key))}>
                <td>{when(it.summary.endedAt)}</td>
                <td>{kindLabel(it)}</td>
                <td>{it.summary.mapName || ""}</td>
                <td>{it.summary.players}</td>
                <td>{it.summary.rounds}</td>
                <td className={styles.humans}>{it.summary.humans.join(", ")}</td>
                <td>{it.summary.deathReports || ""}</td>
                <td>{Math.round(it.size / 1024)} KB</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {state === "loading" && <p className={styles.state}>Loading…</p>}
      {state === "ready" && cursor && <button className={styles.btn} onClick={() => load(cursor)}>Load more</button>}
    </div>
  );
}
